"use client";

import { ReactNode } from "react";
import { ToastProvider } from "@/contexts/ToastContext";
import { MobileMenuProvider } from "@/contexts/MobileMenuContext";
import { SystemSettingsProvider } from "@/contexts/SystemSettingsContext";
import SupabaseProvider from "@/components/SupabaseProvider";
import { LocationProvider } from "@/contexts/LocationContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminProductsAttentionProvider } from "@/contexts/AdminProductsAttentionContext";
import { NotificationUserRealtimeSubscriber } from "@/components/notifications/NotificationUserRealtimeSubscriber";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { Providers } from "@/app/providers";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ThemeMetaSync from "@/components/ThemeMetaSync";
import PostHogProvider from "@/components/providers/PostHogProvider";
import type { ColorModePreference } from "@/lib/design-system/theme";

/**
 * Consolidated App Providers Component
 * Reduces nesting by combining related providers
 *
 * Provider order matters:
 * 1. Analytics/Base providers (Vercel Analytics)
 * 2. Toast (needed by many components)
 * 3. Supabase (needed by auth and location)
 * 4. System Settings (needed by location)
 * 5. Location (needed by auth)
 * 6. Auth (depends on Supabase and Location)
 * 7. Mobile Menu (UI state)
 */
export default function AppProviders({
  children,
  initialColorModePreference = "system",
}: {
  children: ReactNode;
  initialColorModePreference?: ColorModePreference;
}) {
  return (
    <PostHogProvider>
      <Providers>
        <ThemeProvider initialPreference={initialColorModePreference}>
          <ThemeMetaSync />
          <ToastProvider>
            <SupabaseProvider>
              <SystemSettingsProvider>
                <LocationProvider>
                  <AuthProvider>
                    <AdminProductsAttentionProvider>
                      <NotificationUserRealtimeSubscriber />
                      <OnboardingProvider>
                        <MobileMenuProvider>{children}</MobileMenuProvider>
                      </OnboardingProvider>
                    </AdminProductsAttentionProvider>
                  </AuthProvider>
                </LocationProvider>
              </SystemSettingsProvider>
            </SupabaseProvider>
          </ToastProvider>
        </ThemeProvider>
      </Providers>
    </PostHogProvider>
  );
}
